import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, KeyRound, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

export default function PasswordResetConfirmPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error(t.reset.passwordTooShort);
      return;
    }
    if (pwd !== pwd2) {
      toast.error(t.reset.passwordMismatch);
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, t.reset.linkInvalid));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ag-paper ag-paper-grain flex items-center justify-center p-6">
        <div className="max-w-md ag-card p-8 text-center">
          <h1 className="font-serif text-2xl text-ag-ink mb-2">{t.reset.linkInvalid}</h1>
          <p className="text-sm text-slate-500 mb-6">{t.reset.linkInvalidSub}</p>
          <Button asChild className="bg-ag-ink hover:bg-slate-800 h-11 rounded-xl">
            <Link to="/passwort-vergessen">{t.reset.requestNew}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-ag-paper ag-paper-grain">
      <div className="hidden lg:flex w-1/2 ag-sidebar text-slate-100 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-amber-400/10 blur-3xl" />
        <div className="relative">
          <div className="font-serif text-3xl leading-none">ALLES</div>
          <div className="font-serif text-3xl leading-none">GEREGELT.</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mt-2">{t.appTag}</div>
        </div>
        <div className="relative max-w-md">
          <Shield className="h-7 w-7 text-amber-300 mb-6" strokeWidth={1.3} />
          <p className="font-serif text-3xl leading-snug text-white/95">{t.reset.confirmHeroLine}</p>
        </div>
        <div className="relative text-xs text-slate-500">© ALLES GEREGELT · {new Date().getFullYear()}</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md ag-fade-in">
          <Link to="/login" data-testid="reset-confirm-back" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-ag-ink mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> {t.reset.backToLogin}
          </Link>
          <h1 className="font-serif text-4xl text-ag-ink mb-2">{t.reset.confirmTitle}</h1>
          <p className="text-sm text-slate-600 mb-8 leading-relaxed">{t.reset.confirmSub}</p>

          {!done ? (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-confirm-form">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.reset.newPassword}</Label>
                <Input
                  type="password" required value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  data-testid="reset-confirm-pwd"
                  className="mt-1.5 h-11 bg-white border-slate-200"
                  placeholder="Mindestens 8 Zeichen"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.reset.repeatPassword}</Label>
                <Input
                  type="password" required value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  data-testid="reset-confirm-pwd2"
                  className="mt-1.5 h-11 bg-white border-slate-200"
                />
              </div>
              <Button
                type="submit" disabled={loading}
                data-testid="reset-confirm-submit"
                className="w-full h-11 bg-ag-ink hover:bg-slate-800 text-white rounded-xl"
              >
                <KeyRound className="h-4 w-4 mr-2" /> {t.reset.savePassword}
              </Button>
            </form>
          ) : (
            <div data-testid="reset-confirm-done" className="ag-card p-7 border-emerald-200 bg-emerald-50/40">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <Check className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <div className="font-serif text-xl text-ag-ink mb-1">{t.reset.doneTitle}</div>
                  <p className="text-sm text-slate-600">{t.reset.doneSub}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
